import { useYardStores } from '@yard/AppProvider'
import { useCallback, useMemo } from 'react'
import { Node } from 'reactflow'
import { FlexibleBlockWithOccupanciesAndActions } from '../models/yard-view.model'
import { FlexibleBlockViewStore } from '../stores/nodes/flexible-block.view-store'
import {
  BlockColorDistribution,
  resolveSchemeDisplayName,
  YardViewStore,
} from '../stores/yard.view-store'

const FOCUSED_Z_INDEX = 1000

export const useFlexibleBlockNodes = (
  viewStore: YardViewStore,
  flexibleBlockViewStore: FlexibleBlockViewStore,
): Node<FlexibleBlockWithOccupanciesAndActions>[] => {
  const { companyStore } = useYardStores()
  const onDoubleClick = useCallback(
    (blockId: string) => {
      viewStore.setFocusedBlockId(blockId)
      viewStore.setContainerDetailsOpen(true)
    },
    [viewStore],
  )

  // Resolve operator ids to company display names so the FlexibleBlockNode
  // tooltip can render labels without reaching companyStore itself (it's
  // outside the react-flow node context).
  const resolveDistribution = (
    rawDistribution: BlockColorDistribution,
  ): BlockColorDistribution => {
    if (viewStore.colorScheme !== 'operator') return rawDistribution
    return {
      ...rawDistribution,
      segments: rawDistribution.segments.map(s => ({
        ...s,
        label: resolveSchemeDisplayName('operator', s.value, companyStore),
      })),
    }
  }

  return useMemo<Node<FlexibleBlockWithOccupanciesAndActions>[]>(
    () =>
      viewStore.flexibleBlocks.map(block => ({
        id: block.id,
        type: 'flexibleBlock',
        ...(viewStore.focusedBlockId === block.id && { zIndex: FOCUSED_Z_INDEX }),
        position: {
          x: block.layoutPosition.x,
          y: block.layoutPosition.y,
        },
        data: {
          ...block,
          isEditable: !viewStore.isViewMode,
          showNodeHeaders: viewStore.showNodeHeaders,
          distribution: resolveDistribution(viewStore.blockDistribution(block.id)),
          isRestSelected: viewStore.isRestSelected,
          focused: viewStore.focusedBlockId === block.id,
          onEdit: flexibleBlockViewStore.openFormDialog,
          onFocus: viewStore.setFocusedBlockId,
          onRotate: flexibleBlockViewStore.setRotation,
          onDelete: flexibleBlockViewStore.setDeleteDialogOpen,
          onDoubleClick,
        },
      })),
    [
      viewStore.flexibleBlocks,
      viewStore.focusedBlockId,
      viewStore.isViewMode,
      viewStore.showNodeHeaders,
      viewStore.colorScheme,
      viewStore.selectedValuesForActiveScheme,
      viewStore.isRestSelected,
      viewStore.setFocusedBlockId,
      flexibleBlockViewStore,
      companyStore,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      companyStore.companies.length,
      onDoubleClick,
    ],
  )
}
